// Page Load event — tracks page visits (spec section: Page Load)

import { pushAdobeEvent, getCleanUrl } from './core';

export type PageLoadParams = {
  pageTitle?: string;
  locale?: string;
  isLoggedIn?: boolean;
  custKey?: string | null;
};

// UTM campaign parameters parsed from the page URL query string (only present ones are included)
export type UtmPayload = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmID?: string;
  utmTerm?: string;
  utmContent?: string;
  utmPlacement?: string;
};

// Maps each Adobe payload UTM key to its corresponding URL query parameter name
const UTM_PARAM_MAP: Record<keyof UtmPayload, string> = {
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  utmID: 'utm_id',
  utmTerm: 'utm_term',
  utmContent: 'utm_content',
  utmPlacement: 'utm_placement',
};

// Shape of the `page` block attached to every Adobe Data Layer event for page context
export type PagePayload = {
  pageTitle: string;
  urlClean: string;
  urlFull: string;
  domain: string;
  campaignQuery: string;
  previousPage: string;
  language: string;
  deviceType: 'Desktop' | 'Mobile' | 'Tablet';
  isLoggedIn: 'TRUE' | 'FALSE';
  custKey: string | null;
} & UtmPayload;

// Maps Contentstack locale codes to the human-readable language labels Adobe expects
const LANGUAGE_LABELS: Record<string, string> = {
  'en-us': 'English',
  en: 'English',
  es: 'Spanish',
};

// Tracks the previous SPA URL (clean: origin + path only) so navigation events can report `previousPage`
let _previousUrl = '';

// Caches the most recently built `page` payload so click/download events can reuse it
let _currentPagePayload: PagePayload | null = null;

// Returns the Adobe-friendly language label for a Contentstack locale; defaults to English
const getLanguageLabel = (locale?: string): string => {
  if (!locale) return 'English';
  return LANGUAGE_LABELS[locale.toLowerCase()] ?? 'English';
};

// Parses the standard UTM campaign parameters from a URL query string; omits any that are absent
const getUtmParams = (search: string): UtmPayload => {
  const params = new URLSearchParams(search);
  const utm: UtmPayload = {};
  for (const [key, param] of Object.entries(UTM_PARAM_MAP) as [keyof UtmPayload, string][]) {
    const value = params.get(param);
    if (value !== null) utm[key] = value;
  }
  return utm;
};

// sessionStorage key holding the landing campaign query so UTM attribution survives navigation
const CAMPAIGN_STORAGE_KEY = 'srp_adobe_campaign';

// Returns true when a query string carries at least one UTM/campaign parameter
const hasUtmParams = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return Object.values(UTM_PARAM_MAP).some((param) => params.has(param));
};

// Reads the persisted landing campaign query from sessionStorage; '' when none or storage is unavailable
const getStoredCampaignQuery = (): string => {
  try {
    return sessionStorage.getItem(CAMPAIGN_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
};

// Persists the landing campaign query for the rest of the session (best-effort; ignores private-mode errors)
const storeCampaignQuery = (search: string): void => {
  try {
    sessionStorage.setItem(CAMPAIGN_STORAGE_KEY, search);
  } catch {
    // sessionStorage can throw in private mode or when disabled — degrade silently
  }
};

// Resolves the session-persistent campaign query: a UTM-bearing current URL wins and is stored,
// otherwise the stored landing campaign is reused so params persist across page views
const resolveCampaignQuery = (currentSearch: string): string => {
  const cleaned = currentSearch.replace(/^\?/, '');
  if (hasUtmParams(currentSearch)) {
    storeCampaignQuery(cleaned);
    return cleaned;
  }
  return getStoredCampaignQuery() || cleaned;
};

// Heuristic device classification from the user agent
const getDeviceType = (): 'Desktop' | 'Mobile' | 'Tablet' => {
  if (typeof navigator === 'undefined') return 'Desktop';
  const ua = navigator.userAgent;
  if (/iPad|Tablet/i.test(ua)) return 'Tablet';
  if (/Mobi|Android|iPhone/i.test(ua)) return 'Mobile';
  return 'Desktop';
};

// Returns the most recently built `page` payload so other events can attach the same page context
export const getCurrentPagePayload = (): PagePayload | null => _currentPagePayload;

// Pushes a Page Load event to the Adobe Client Data Layer matching the SRP analytics schema
export const trackAdobePageView = (params?: PageLoadParams): void => {
  if (typeof window === 'undefined') return;

  const pageTitle = params?.pageTitle ?? document.title;
  const urlClean = window.location.pathname;
  const urlFull = window.location.href;
  // Use the previously tracked SPA URL when available; fall back to the document referrer on first load.
  // Always cleaned to origin + path so query params and hash never leak into previousPage.
  const previousPage = _previousUrl || getCleanUrl(document.referrer) || '';
  _previousUrl = getCleanUrl(urlFull);

  const eventName = 'Page View';

  // Campaign query persists for the session so UTM params carry across pages after the landing page
  const campaignQuery = resolveCampaignQuery(window.location.search);

  const page: PagePayload = {
    pageTitle,
    urlClean,
    urlFull,
    domain: window.location.hostname,
    campaignQuery,
    previousPage,
    language: getLanguageLabel(params?.locale),
    deviceType: getDeviceType(),
    isLoggedIn: params?.isLoggedIn ? 'TRUE' : 'FALSE',
    custKey: params?.custKey ?? null,
    ...getUtmParams(campaignQuery),
  };
  _currentPagePayload = page;

  // eventName/eventDesc sit alongside `event` (not nested in `page`)
  pushAdobeEvent({
    event: 'Page Load',
    eventName,
    eventDesc: `${eventName} - ${pageTitle}`,
    page,
  });
};
